import { forwardRef, useId, useState, type AnchorHTMLAttributes, type ReactNode } from 'react'
import { HiArrowUpRight, HiOutlineSquares2X2 } from 'react-icons/hi2'
import { WorkspaceLink } from '../../common/workspaceNavigation'
import { toSafeExternalUrl } from '../../common/safeExternalUrl'
import { DropdownMenuItem } from '../../components/ui/dropdown-menu'
import { iconIsComponent, type HubLinkEntry, type HubLinkSection } from './hubLinks'
import './hub-links.css'

export interface HubLinkRenderProps {
  renderBadge?: (link: HubLinkEntry) => ReactNode
  /** Lets the header keep live icons, such as the System Development status light. */
  renderIcon?: (link: HubLinkEntry) => ReactNode | undefined
  isActive?: (link: HubLinkEntry) => boolean
  needsAttention?: (link: HubLinkEntry) => boolean
}

export function LinkIcon({ link, renderIcon }: { link: HubLinkEntry; renderIcon?: HubLinkRenderProps['renderIcon'] }) {
  const [failedSource, setFailedSource] = useState<string | null>(null)
  const custom = renderIcon?.(link)
  if (custom) return <>{custom}</>
  if (iconIsComponent(link.icon)) {
    const Icon = link.icon
    return <Icon aria-hidden="true" />
  }
  if (typeof link.icon === 'string' && link.icon !== failedSource) {
    return <img src={link.icon} alt="" aria-hidden="true" loading="lazy" decoding="async"
      onError={() => setFailedSource(link.icon as string)} />
  }
  return <HiOutlineSquares2X2 aria-hidden="true" />
}

type AnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & { link: HubLinkEntry; active?: boolean }

// forwardRef so Radix menu items can manage focus on the rendered anchor.
export const HubLinkAnchor = forwardRef<HTMLAnchorElement, AnchorProps>(function HubLinkAnchor({ link, active, children, ...props }, ref) {
  if (link.external) {
    return <a {...props} ref={ref} href={toSafeExternalUrl(link.to) || undefined} target="_blank" rel="noopener noreferrer"
      aria-label={`${link.label} (opens in a new tab)`}>{children}</a>
  }
  return <WorkspaceLink {...props} ref={ref} to={link.to} aria-current={active ? 'page' : undefined}>{children}</WorkspaceLink>
})

/** One group's links as menu items, for the header menus. */
export function HubLinkMenuItems({ links, ...render }: HubLinkRenderProps & { links: HubLinkEntry[] }) {
  return <>{links.map(link => <DropdownMenuItem key={link.key} asChild className="hub-menu-item">
    <HubLinkAnchor link={link} active={render.isActive?.(link)} className="hub-menu-row">
      <span className="hub-row-icon"><LinkIcon link={link} renderIcon={render.renderIcon} /></span>
      <span className="hub-row-label">{link.label}</span>
      {render.renderBadge?.(link)}
      {link.external && <HiArrowUpRight className="hub-row-external" aria-hidden="true" />}
    </HubLinkAnchor>
  </DropdownMenuItem>)}</>
}

export function AttentionDot({ label = 'Something here needs your attention' }: { label?: string }) {
  return <span className="hub-attention-dot" role="img" aria-label={label} />
}

/** Every group and its links, for the home page sidebar. */
export default function HubLinkDirectory({ sections, loading = false, ...render }: HubLinkRenderProps & {
  sections: HubLinkSection[]
  loading?: boolean
}) {
  const id = useId()
  if (loading) {
    return <div className="hub-dir hub-dir--sidebar hub-dir--loading" role="status" aria-label="Loading your links">
      {[5, 3, 4].map((count, group) => <div className="hub-dir-skeleton-group" key={group} aria-hidden="true">
        <span className="hub-skeleton hub-skeleton-heading" />
        {Array.from({ length: count }, (_, index) => <span className="hub-skeleton hub-skeleton-row" key={index} />)}
      </div>)}
    </div>
  }
  return <nav className="hub-dir hub-dir--sidebar" aria-label="All links">
    {sections.map(({ group, links }) => <section key={group.id} className="hub-side-group" aria-labelledby={`${id}-${group.id}`}>
      <h2 id={`${id}-${group.id}`} className="hub-side-heading">{group.label}</h2>
      <ul>{links.map(link => <li key={link.key}>
        <HubLinkAnchor link={link} active={render.isActive?.(link)} className="hub-side-row">
          <span className="hub-row-icon"><LinkIcon link={link} renderIcon={render.renderIcon} /></span>
          <span className="hub-row-label">{link.label}</span>
          {render.renderBadge?.(link)}
          {link.external && <HiArrowUpRight className="hub-row-external" aria-hidden="true" />}
        </HubLinkAnchor>
      </li>)}</ul>
    </section>)}
  </nav>
}
